/* SpawnAnything — browser side of photo -> voxel figure.
   prepareImage(): decode, downscale to <= 512 px and re-encode as JPEG (drops EXIF/GPS and all other metadata).
   generatePhoto(): POST /api/generate-photo; never throws, re-validates the recipe, falls back locally. */
import { isBlocked } from '../game/blocklist.js';
import { validateUnitRecipe, fallbackUnitRecipe } from '../game/recipeSchema.js';

export const PHOTO_MAX_SIDE = 512;
export const PHOTO_QUALITY = 0.8;
export const PHOTO_MAX_INPUT = 30 * 1024 * 1024; // larger source files are rejected before decoding
export const HINT_MAX = 60;

async function decode(file) {
  // imageOrientation 'from-image' applies the EXIF rotation before the metadata is dropped
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* fall through (e.g. HEIC on Safari) */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally { URL.revokeObjectURL(url); }
}

/**
 * Downscale a user image in the browser. Only the re-encoded JPEG leaves the device.
 * @param {Blob} file
 * @returns {Promise<{ base64: string, dataUrl: string, width: number, height: number, bytes: number }>}
 */
export async function prepareImage(file, { maxSide = PHOTO_MAX_SIDE, quality = PHOTO_QUALITY } = {}) {
  if (!file || !/^image\//.test(file.type || 'image/')) throw new Error('Das ist kein Bild.');
  if (file.size > PHOTO_MAX_INPUT) throw new Error('Das Bild ist zu groß (max. 30 MB).');
  let src;
  try { src = await decode(file); } catch { throw new Error('Das Bild konnte nicht gelesen werden.'); }
  const w0 = src.width || src.naturalWidth, h0 = src.height || src.naturalHeight;
  if (!w0 || !h0) throw new Error('Das Bild konnte nicht gelesen werden.');
  const s = Math.min(1, maxSide / Math.max(w0, h0));
  const width = Math.max(1, Math.round(w0 * s)), height = Math.max(1, Math.round(h0 * s));
  const cv = document.createElement('canvas');
  cv.width = width; cv.height = height;
  const g = cv.getContext('2d');
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, width, height); // transparent PNGs -> white, not black
  g.imageSmoothingQuality = 'high';
  g.drawImage(src, 0, 0, width, height);
  if (typeof src.close === 'function') src.close();
  const dataUrl = cv.toDataURL('image/jpeg', quality);
  cv.width = cv.height = 0; // free the pixels right away
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return { base64, dataUrl, width, height, bytes: Math.floor(base64.length * 3 / 4) };
}

/**
 * @param {string} base64  JPEG/PNG/WebP, base64 without data: prefix
 * @param {string} [hint]  short wish, e.g. "mit Laserschwert"
 * @returns {Promise<{ recipe: object, source: 'model'|'fallback'|'offline', cached: boolean, code?: string, message?: string }>}
 */
export async function generatePhoto(base64, hint = '', { timeoutMs = 90000, endpoint = '/api/generate-photo', signal } = {}) {
  let h = typeof hint === 'string' ? Array.from(hint.replace(/[\u0000-\u001f\u007f<>]/g, ' ').trim()).slice(0, HINT_MAX).join('') : '';
  if (isBlocked(h)) h = ''; // block list: a forbidden wish is dropped, the photo alone still makes a figure
  const fbName = h || 'Foto-Figur';
  const offline = (code, message) => ({ recipe: fallbackUnitRecipe(fbName), source: 'offline', cached: false, code, message });
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onAbort = () => ctrl.abort();
  if (signal) { if (signal.aborted) ctrl.abort(); else signal.addEventListener('abort', onAbort, { once: true }); }
  try {
    const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image: base64, hint: h }), signal: ctrl.signal });
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    if (res.status === 429) return offline('rate_limited', 'Zu viele Fotos in kurzer Zeit. Warte einen Moment und versuche es noch einmal.');
    if (res.status === 413 || res.status === 415 || (res.status === 400 && /image/.test(data?.error || ''))) return offline(data?.error || `http_${res.status}`, 'Dieses Bild kann nicht verwendet werden. Versuche ein anderes Foto (JPEG oder PNG).');
    if (!res.ok || !data || typeof data !== 'object') return offline(`http_${res.status}`, 'Der KI-Dienst ist nicht erreichbar.');
    const source = data.source === 'model' ? 'model' : 'fallback';
    const message = typeof data.message === 'string' ? data.message.slice(0, 300) : undefined;
    const v = validateUnitRecipe(data.recipe, { fallbackName: fbName });
    if (!v.ok) return { recipe: fallbackUnitRecipe(fbName), source: 'fallback', cached: false, code: 'invalid_recipe', message };
    return { recipe: v.recipe, source, cached: !!data.cached, code: data.code, message };
  } catch (e) {
    return offline(e && e.name === 'AbortError' ? 'timeout' : 'network', 'Der KI-Dienst ist nicht erreichbar.');
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}
